"use client";

/**
 * The website's enquiry form. It lands on the builder's Website page and,
 * the moment it is sent, on the owner's WhatsApp
 * (convex/construction/website.ts#sendEnquiry). "Ask about it" anywhere on
 * the page fills in the project and the home.
 */

import { useEffect, useState, type FormEvent } from "react";
import { useMutation } from "convex/react";
import { ConvexError } from "convex/values";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@/convex/_generated/api";
import type { Id } from "@/convex/_generated/dataModel";
import { ENQUIRE_EVENT } from "../theme";

const field =
  "w-full rounded-lg border border-[var(--b-line)] bg-white px-3.5 py-2.5 text-base text-[var(--b-ink)] outline-none placeholder:text-[#8a929a] focus:border-[var(--b-ink)] focus:ring-2 focus:ring-[var(--b-accent)]";
const label = "grid gap-1.5 text-sm font-semibold";

export default function EnquiryForm({ brandId, projects }: { brandId: string; projects: Array<{ code: string; name: string }> }) {
  const send = useMutation(api.construction.website.sendEnquiry);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [project, setProject] = useState("");
  const [unit, setUnit] = useState("");
  const [message, setMessage] = useState("");
  const [trap, setTrap] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const onAsk = (e: Event) => {
      const d = (e as CustomEvent<{ project?: string; unit?: string }>).detail ?? {};
      setSent(false);
      setProject(d.project && projects.some((p) => p.code === d.project) ? d.project : "");
      setUnit(d.unit ?? "");
      const projectName = projects.find((p) => p.code === d.project)?.name;
      if (d.unit && projectName) setMessage((m) => m || `Is ${d.unit} at ${projectName} still available? I would like to know the payment plan.`);
      else if (projectName) setMessage((m) => m || `I would like to know more about ${projectName}.`);
    };
    window.addEventListener(ENQUIRE_EVENT, onAsk);
    return () => window.removeEventListener(ENQUIRE_EVENT, onAsk);
  }, [projects]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await send({
        brandId: brandId as Id<"brands">,
        name,
        phone,
        email: email.trim() || undefined,
        message,
        project: project || undefined,
        unit: project && unit ? unit : undefined,
        website: trap || undefined,
      });
      setSent(true);
      setMessage("");
      setUnit("");
    } catch (err) {
      setError(err instanceof ConvexError && typeof err.data === "string" ? err.data : "The message did not go. Check your connection and try again, or call us.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="grid gap-3 rounded-xl border-2 border-[var(--b-ink)] bg-[var(--b-ground)] p-6" role="status">
        <CheckCircle2 className="h-8 w-8 text-[var(--b-accent-ink)]" />
        <p className="text-lg font-extrabold">Thank you, {name.split(" ")[0] || "we have it"}.</p>
        <p className="text-[var(--b-muted)]">We have your message and will call you back on the number you gave.</p>
        <button type="button" onClick={() => setSent(false)} className="justify-self-start text-sm font-bold text-[var(--b-accent-ink)] hover:underline">
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid gap-4" noValidate>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className={label}>
          Your name
          <input className={field} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" required maxLength={80} />
        </label>
        <label className={label}>
          Phone
          <input className={field} value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" inputMode="tel" placeholder="0772 123 456 or +44 …" required />
        </label>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className={label}>
          <span>
            Email <span className="font-normal text-[var(--b-muted)]">(if you like)</span>
          </span>
          <input className={field} value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" inputMode="email" type="email" />
        </label>
        {projects.length > 0 && (
          <label className={label}>
            About
            <select
              className={field}
              value={project}
              onChange={(e) => {
                setProject(e.target.value);
                setUnit("");
              }}
            >
              <option value="">Something else</option>
              {projects.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {project && unit && (
        <p className="text-sm text-[var(--b-muted)]">
          About home <b className="text-[var(--b-ink)]">{unit}</b>.{" "}
          <button type="button" onClick={() => setUnit("")} className="font-semibold text-[var(--b-accent-ink)] hover:underline">
            Not a particular home
          </button>
        </p>
      )}
      <label className={label}>
        Your message
        <textarea className={`${field} min-h-[120px] resize-y`} value={message} onChange={(e) => setMessage(e.target.value)} required maxLength={1500} />
      </label>
      {/* People never see this; form-filling robots do and fill it in. */}
      <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
        <label>
          Leave this empty
          <input tabIndex={-1} autoComplete="off" value={trap} onChange={(e) => setTrap(e.target.value)} />
        </label>
      </div>
      {error && (
        <p className="rounded-lg border border-[#b4232a]/40 bg-[#b4232a]/5 px-3.5 py-2.5 text-sm text-[#8f1c22]" role="alert">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={busy}
        className="inline-flex items-center justify-center gap-2 justify-self-start rounded-lg bg-[var(--b-accent)] px-6 py-3 text-base font-bold text-[var(--b-on-accent)] disabled:opacity-60"
      >
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send
      </button>
    </form>
  );
}
